import React, { useEffect, useState } from "react";
import { Button } from "../../components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Megaphone, CheckCircle2, Paperclip, Volume2, Clock,
} from "lucide-react";
import VoicePlayer from "@/components/VoicePlayer";

/**
 * Phase 5M — Portal Announcements (Broadcast feed)
 */
export default function PortalAnnouncements() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/broadcasts/my-feed");
      setItems(data.items || []);
      setUnread(data.unread_count || 0);
    } catch (e) {
      toast.error("Failed to load announcements");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const markRead = async (bid) => {
    try {
      await api.post(`/broadcasts/${bid}/mark-read`);
      await load();
    } catch (e) {
      toast.error("Failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="portal-announcements">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400 flex items-center gap-1.5">
            <Megaphone className="h-3 w-3" /> Company Announcements
          </div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
            Announcements
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">
            Important messages and updates from HR and management.
          </p>
        </div>
        {unread > 0 && (
          <div className="bg-red-600 text-white px-3 py-1.5 rounded-full text-xs font-bold" data-testid="unread-badge">
            {unread} unread
          </div>
        )}
      </div>

      {loading && <div className="text-center py-12 text-xs text-slate-400">Loading…</div>}

      {!loading && items.length === 0 && (
        <div className="text-center py-16 border border-dashed border-slate-300 rounded-lg bg-white" data-testid="no-announcements">
          <Megaphone className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-500 font-medium">No announcements yet</p>
          <p className="text-xs text-slate-400 mt-1">When HR or management broadcasts a message, it will appear here.</p>
        </div>
      )}

      <div className="space-y-3">
        {items.map(it => {
          const unreadItem = !it.read_at;
          return (
            <div key={it.recipient_id} className={`border rounded-lg p-4 ${unreadItem ? "bg-red-50/40 border-red-300" : "bg-white border-slate-200"}`} data-testid={`announcement-${it.broadcast_id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-bold text-slate-900">{it.title}</h3>
                    {unreadItem && <span className="text-[9px] bg-red-600 text-white px-1.5 py-0.5 rounded font-bold uppercase">NEW</span>}
                  </div>
                  <div className="flex items-center gap-2 mt-1 text-[10px] text-slate-500">
                    <span>From <b>{it.sender_name}</b></span>
                    <span className="text-slate-300">·</span>
                    <Clock className="h-3 w-3" />
                    <span>{new Date(it.created_at).toLocaleString()}</span>
                  </div>
                  <p className="text-xs text-slate-700 mt-2 whitespace-pre-wrap">{it.message}</p>

                  {/* Voice */}
                  {it.voice_url && (
                    <div className="mt-2 flex items-center gap-2 bg-violet-50 border border-violet-200 rounded p-2">
                      <Volume2 className="h-3.5 w-3.5 text-violet-700" />
                      <VoicePlayer src={it.voice_url} className="h-8 flex-1" data-testid={`voice-${it.broadcast_id}`} />
                    </div>
                  )}

                  {/* Attachments */}
                  {it.attachments?.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {it.attachments.map((a, i) => (
                        <a key={i} href={`/api/files/${a.path}`} target="_blank" rel="noreferrer"
                            className="flex items-center gap-2 text-xs bg-slate-50 border border-slate-200 rounded px-2 py-1.5 hover:bg-red-50 hover:border-red-200 transition-colors"
                            data-testid={`attachment-${it.broadcast_id}-${i}`}>
                          <Paperclip className="h-3.5 w-3.5 text-slate-500" />
                          <span className="flex-1 truncate">{a.name}</span>
                          <span className="text-[10px] text-slate-400">{Math.round((a.size || 0) / 1024)} KB</span>
                        </a>
                      ))}
                    </div>
                  )}
                </div>
                {unreadItem && (
                  <Button variant="outline" size="sm" onClick={() => markRead(it.broadcast_id)} className="text-[11px] h-7" data-testid={`mark-read-${it.broadcast_id}`}>
                    <CheckCircle2 className="h-3.5 w-3.5 mr-1" />Mark Read
                  </Button>
                )}
                {!unreadItem && (
                  <span className="text-[10px] text-emerald-600 flex items-center gap-1"><CheckCircle2 className="h-3 w-3" />Read</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
